import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import yup from '../../libs/yup/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { createVoucher } from '../../api/sportaloka-client';
import CustomInput from '../../components/input/CustomInput';
import { AxiosError } from 'axios';

const defaultValues = {
  code: '',
  title_id: '',
  quota: 0,
  title_en: '',
  value: 0,
  valid_from: '',
  valid_thru: ''
};

const schema = yup.object().shape({
  code: yup.string().label('Kode Voucher').required('${label} wajib diisi'),
  title_id: yup.string().label('Judul (ID)').required('${label} wajib diisi'),
  title_en: yup.string().label('Judul (EN)').required('${label} wajib diisi'),
  quota: yup
    .number()
    .typeError('Harus berupa angka')
    .label('Kuota')
    .required('${label} wajib diisi'),
  value: yup
    .number()
    .typeError('Harus berupa angka')
    .label('Nilai')
    .required('${label} wajib diisi'),
  valid_from: yup.string().label('Berlaku Mulai').required('${label} wajib diisi'),
  valid_thru: yup.string().label('Berlaku Hingga').required('${label} wajib diisi')
});

const CreateVoucher = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = async (val) => {
    try {
      setLoading(true);
      await createVoucher(val);
      navigate('/voucher');
      Swal.fire({
        title: 'Berhasil!',
        text: `Voucher berhasil dibuat`,
        icon: 'success',
        confirmButtonText: 'Tutup'
      });
      setLoading(false);
    } catch (error) {
      let message = 'Periksa koneksi anda dan coba kembali.';
      if (error instanceof AxiosError && error.response.data.code === '100908') {
        message = `Voucher dengan kode '${val.code}' sudah ada di database`;
      }

      Swal.fire({
        title: 'Gagal!',
        text: message,
        icon: 'error',
        confirmButtonText: 'Tutup'
      });
      setLoading(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="row y-gap-20 justify-between items-end pb-60 lg:pb-40 md:pb-32">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="text-30 lh-14 fw-600">Buat Voucher</h1>
            <div className="d-flex">
              <Link to="/voucher" className="button -md bg-white h-50 text-dark mr-20">
                <i className="fa fa-arrow-left mr-10" />
                Kembali
              </Link>

              <button disabled={loading} className="button -md bg-blue-1 h-50 text-white">
                {!loading && <i className="fa fa-save text-16 mr-10" />}
                {loading ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
          <div className="py-30 px-30 rounded-4 bg-white shadow-3 mt-20">
            <div className="row">
              <div className="col-12">
                <Controller
                  control={form.control}
                  name="code"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        isRequired={true}
                        type="text"
                        label="Kode Voucher"
                        isError={Boolean(form.formState.errors.name)}
                        isSolid={true}
                        errorMessage={
                          Boolean(form.formState.errors.name) && form.formState.errors.name.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div className="col-6">
                <Controller
                  control={form.control}
                  name="title_id"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        isRequired={true}
                        type="text"
                        label="Judul (ID)"
                        isError={Boolean(form.formState.errors.name)}
                        isSolid={true}
                        errorMessage={
                          Boolean(form.formState.errors.name) && form.formState.errors.name.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div className="col-6">
                <Controller
                  control={form.control}
                  name="title_en"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        isRequired={true}
                        type="text"
                        label="Judul (EN)"
                        isError={Boolean(form.formState.errors.name)}
                        isSolid={true}
                        errorMessage={
                          Boolean(form.formState.errors.name) && form.formState.errors.name.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div className="col-6">
                <Controller
                  control={form.control}
                  name="quota"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        isRequired={true}
                        type="number"
                        label="Jumlah Kuota"
                        isError={Boolean(form.formState.errors.name)}
                        isSolid={true}
                        errorMessage={
                          Boolean(form.formState.errors.name) && form.formState.errors.name.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div className="col-6">
                <Controller
                  control={form.control}
                  name="value"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        isRequired={true}
                        type="number"
                        label="Nilai (IDR)"
                        isError={Boolean(form.formState.errors.name)}
                        isSolid={true}
                        errorMessage={
                          Boolean(form.formState.errors.name) && form.formState.errors.name.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div className="col-6">
                <Controller
                  control={form.control}
                  name="valid_from"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        disabled={loading}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        type="date"
                        isRequired={true}
                        label="Mulai Berlaku"
                        isError={Boolean(form.formState.errors.valid_from)}
                        errorMessage={
                          Boolean(form.formState.errors.valid_from) &&
                          form.formState.errors.valid_from.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div className="col-6">
                <Controller
                  control={form.control}
                  name="valid_thru"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mb-20">
                      <CustomInput
                        disabled={loading}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        type="date"
                        isRequired={true}
                        label="Berlaku Hingga"
                        isError={Boolean(form.formState.errors.valid_thru)}
                        errorMessage={
                          Boolean(form.formState.errors.valid_thru) &&
                          form.formState.errors.valid_thru.message
                        }
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateVoucher;
